import * as a from '@mitranim/js/all.mjs'
import * as ue from './util-err.mjs'
import * as ut from './util-text.mjs'
import * as c from './conf.mjs'

const HEADER_NAME_NO_CACHE = `no-cache`
const HEADER_NAME_COOKIE = `cookie`

export function ctxFetch(ctx, {url, timeout, ...opt}) {
  if (!opt.keepalive) {
    timeout ??= c.NET_TIMEOUT_LONG
    opt.signal = ctx?.signal
  }

  url = ut.toUrl(url)
  if (!url.origin || (url.origin === ctx?.env.baseUrl?.origin)) {
    normHead(ctx, opt)
  }

  if (timeout) return fetchWithTimeout(url, opt, timeout)
  return fetch(url, opt)
}

function normHead(ctx, opt) {
  const src = ctx?.env.headers
  const tar = opt.headers = toArr(opt.headers)

  /*
  We don't simply copy all headers from `ctx.env.headers`, because that would
  be incorrect. For example, the incoming request to our SSR server from a
  user's browser might have `Accept: text/html`, but various API requests
  from the SSR server to the API server in the context of this HTML request
  would have `Accept: application/json` and such. There are probably many
  headers we don't want to copy.
  */
  headAddOpt(tar, a.HEADER_NAME_CACHE_CONTROL, HEADER_NAME_NO_CACHE)
  headAddOpt(tar, HEADER_NAME_COOKIE, src.get(HEADER_NAME_COOKIE))
  headAddOpt(tar, c.HEADER_NAME_SESSION, src.get(c.HEADER_NAME_SESSION))
  headAddOpt(tar, c.HEADER_NAME_PLATFORM, window.navigator?.platform)
  headAddOpt(tar, c.HEADER_NAME_APP_TYPE, `web`)
  headAddOpt(tar, c.HEADER_NAME_APP_VER, c.VERSION)

  /*
  "Test mode" is supported by the backend and has special behaviors, such as
  stashing emails instead of sending them. The test mode ctx var should be set
  by tests directly in the browser. Currently they set it dynamically, after
  script initialization, therefore we have to read it dynamically.
  */
  if (window.process.env.TEST_MODE === `true`) {
    tar.push([c.HEADER_NAME_TEST_MODE, ``])
  }

  // Also used by tests.
  headAddOpt(tar, c.HEADER_NAME_PRIVATE_TOKEN, window.process.env.APP_PRIVATE_API_TOKEN)
}

function toArr(val) {
  return Object.isFrozen(val) ? a.arrCopy(val) : a.laxArr(val)
}

async function fetchWithTimeout(url, opt, timeout) {
  const ctx = new a.Ctx(opt?.signal)
  opt.signal = ctx.signal

  let timer = setTimeout(function onTimeout() {
    timer = undefined
    // Reason is provided because some browsers seem to require it.
    ctx.abort(`timeout`)
  }, timeout)

  try {
    return await fetch(url, opt)
  }
  catch (err) {
    if (ue.isErrAbort(err) && !timer) throw new ue.ErrHttpTimeout(timeout)
    throw err
  }
  finally {
    clearTimeout(timer)
  }
}

function headAddOpt(tar, key, val) {
  a.reqArr(tar)
  a.reqValidStr(key)
  val = a.renderLax(val)
  if (val) tar.push([key, val])
}

/*
Used for storing DOM elements representing the page to be rendered. In SSR, this
is eventually converted to an HTML string, but in browsers, the DOM nodes are
directly moved to the DOM.

The input `html` must conform to the following TypeScript definition:

  interface Html {
    head?: Array<HTMLElement>
    body?: HTMLElement
  }
*/
export class HtmlResponse extends Response {
  constructor(html, opt) {
    super(undefined, opt)
    this.html = a.optObj(html)
    this.headers.set(a.HEADER_NAME_CONTENT_TYPE, a.MIME_TYPE_HTML)
  }
}

export function resHtml(...src) {return new HtmlResponse(...src)}

export function resRedir(status, location) {
  return new Response(undefined, {
    status: a.req(status, a.isStatusRedir),
    headers: {location: a.reqScalar(location)},
  })
}

// Short for "descending to direction". Converts boolean to ordering direction
// string compatible with our backend.
export function descToDir(desc) {
  if (a.isNil(desc)) return undefined
  if (a.optBool(desc)) return c.DIR_DESC
  return c.DIR_ASC
}

export async function* streamIter(src) {
  const read = a.reqInst(src, ReadableStream).getReader()
  const dec = new TextDecoder()
  try {
    for (;;) {
      const {done, value} = await read.read()
      if (done) return
      yield dec.decode(value, {stream: true})
    }
  }
  finally {
    read.releaseLock()
  }
}

export async function* streamLines(src) {
  const read = a.reqInst(src, ReadableStream).getReader()
  const dec = new TextDecoder()
  let buf = ``

  try {
    for (;;) {
      const {done, value} = await read.read()
      if (done) break
      buf += dec.decode(value, {stream: true})
      const lines = buf.split(/[\r\n]+/)
      buf = lines.pop() || `` // If ends with newline, this pops an empty string.
      for (const val of lines) if (val) yield val // Skip empty lines.
    }
    if (buf) yield buf
  }
  finally {
    read.releaseLock()
  }
}
